import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../utils/api';

const StudentStatusPrint = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    
    const status = queryParams.get('status');
    const regionId = queryParams.get('region_id');
    const classId = queryParams.get('school_class_id');
    const academicYearId = queryParams.get('academic_year_id');
    const search = queryParams.get('search');
    const isActive = queryParams.get('is_active');
    const groupId = queryParams.get('group_id');
    const institutionId = queryParams.get('institution_id');
    const includeInactive = queryParams.get('include_inactive');
    const [selectedYearName, setSelectedYearName] = useState('');

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                // Fetch the year name if we have an ID
                if (academicYearId) {
                    try {
                        const res = await api.get('/academic-years');
                        const years = Array.isArray(res.data) ? res.data : (res.data.data || []);
                        const year = years.find(y => String(y.id) === String(academicYearId));
                        if (year) setSelectedYearName(year.name);
                    } catch (err) {
                        console.error("Error fetching academic year name:", err);
                    }
                }

                const params = {
                    all: 'true',
                    status,
                    region_id: regionId,
                    school_class_id: classId,
                    academic_year_id: academicYearId,
                    search,
                    group_id: groupId,
                    institution_id: institutionId,
                    include_inactive: includeInactive
                };

                // Only include is_active if it's a specific value to avoid backend confusion with null/empty
                if (isActive !== null && isActive !== undefined && isActive !== '') {
                    params.is_active = isActive;
                }

                const response = await api.get(`/students`, { params });
                setStudents(response.data?.data || response.data || []);
                setLoading(false);
                
                // Trigger print after data is loaded and rendered
                setTimeout(() => {
                    window.print();
                }, 1000);
            } catch (err) {
                console.error("Error fetching students for print:", err);
                setLoading(false);
            }
        };

        fetchStudents();
    }, [location.search]);

    if (loading) {
        return <div className="p-10 text-center">جاري تحميل البيانات...</div>;
    }

    return (
        <div className="print-page" dir="rtl">
            <div className="header">
                <div className="association-info">
                    <img src="/logo.png" alt="Logo" className="logo" />
                    <div className="text text-right">
                        <h1>جمعية تنمية النقل المدرسي</h1>
                        <h2>ولاد دحو</h2>
                    </div>
                </div>
                <div className="report-title">
                    <h3>لائحة التلاميذ</h3>
                    <div className="filters-summary">
                        {status && <span>الحالة: {status}</span>}
                        {isActive !== null && (
                            <span> | الوضعية: {isActive === '1' ? 'نشطون' : 'مغادرون'}</span>
                        )}
                        {students.length > 0 && students[0].school_class && classId && (
                            <span> | القسم: {students[0].school_class.name}</span>
                        )}
                        {students.length > 0 && students[0].group && groupId && (
                            <span> | المجموعة: {students[0].group.name}</span>
                        )}
                        {students.length > 0 && students[0].region && regionId && (
                            <span> | المنطقة: {students[0].region.region_name}</span>
                        )}
                        {(selectedYearName || (students.length > 0 && students[0].academic_year)) && (
                            <span> | السنة الدراسية: {selectedYearName || students[0].academic_year.name}</span>
                        )}
                        {students.length > 0 && students[0].institution && institutionId && (
                            <span> | المؤسسة: {students[0].institution.name}</span>
                        )}
                        <span> | العدد الإجمالي: {students.length}</span>
                    </div>
                </div>
            </div>

            <table className="print-table">
                <thead>
                    <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '25%' }}>الاسم الكامل</th>
                        <th style={{ width: '15%' }}>رمز QR</th>
                        <th style={{ width: '15%' }}>المؤسسة</th>
                        <th style={{ width: '10%' }}>القسم</th>
                        <th style={{ width: '10%' }}>المنطقة</th>
                        {isActive === '0' ? (
                            <>
                                <th style={{ width: '10%' }}>تاريخ المغادرة</th>
                                <th style={{ width: '10%' }}>سبب المغادرة</th>
                            </>
                        ) : (
                            <th style={{ width: '10%' }}>الحالة</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {students.map((student, index) => (
                        <tr key={student.id}>
                            <td>{index + 1}</td>
                            <td className="font-bold">{student.full_name}</td>
                            <td>{student.qr_code || '--'}</td>
                            <td>{student.institution?.name || '--'}</td>
                            <td>{student.school_class?.name || '--'}</td>
                            <td>{student.region?.region_name || '--'}</td>
                            {isActive === '0' ? (
                                <>
                                    <td>{student.exit_date || '--'}</td>
                                    <td style={{ fontSize: '10px' }}>{student.exit_reason || '--'}</td>
                                </>
                            ) : (
                                <td>
                                    {student.is_active == 1 ? (
                                        <span style={{ color: '#059669', fontWeight: 'bold' }}>نشط</span>
                                    ) : (
                                        <span style={{ color: '#dc2626', fontWeight: 'bold' }}>مغادر</span>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="footer">
                <p>تاريخ الاستخراج: {new Date().toLocaleDateString('ar-MA')}</p>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@400;700&display=swap');
                
                @page {
                    size: A4;
                    margin: 15mm;
                }

                body {
                    font-family: 'Noto Kufi Arabic', sans-serif;
                    margin: 0;
                    padding: 0;
                    color: #333;
                }

                .print-page {
                    width: 100%;
                }

                .header {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: 30px;
                    border-bottom: 2px solid #eea508;
                    padding-bottom: 15px;
                }

                .association-info {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 20px;
                    width: 100%;
                    margin-bottom: 20px;
                }

                .logo {
                    height: 80px;
                    object-fit: contain;
                }

                .header h1 {
                    margin: 0;
                    font-size: 22px;
                    color: #1e293b;
                }

                .header h2 {
                    margin: 0;
                    font-size: 18px;
                    color: #eea508;
                }

                .report-title {
                    text-align: center;
                }

                .report-title h3 {
                    margin: 10px 0;
                    font-size: 20px;
                    text-decoration: underline;
                }

                .filters-summary {
                    font-size: 12px;
                    color: #64748b;
                }

                .print-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 20px;
                }

                .print-table th, .print-table td {
                    border: 1px solid #cbd5e1;
                    padding: 10px 8px;
                    text-align: center;
                    font-size: 12px;
                }

                .print-table th {
                    background-color: #f8fafc;
                    color: #475569;
                    font-weight: 700;
                }

                .print-table tr:nth-child(even) {
                    background-color: #fcfcfc;
                }

                .font-bold {
                    font-weight: 700;
                }

                .footer {
                    margin-top: 40px;
                    text-align: left;
                    font-size: 10px;
                    color: #94a3b8;
                }

                @media print {
                    .no-print {
                        display: none;
                    }
                }
            `}} />
        </div>
    );
};

export default StudentStatusPrint;
